/**
 * The plan the pricing page's "Start trial" carries into signup (task 512).
 *
 * The pricing page links each paid plan to
 * `/signup?plan=professional&period=annual` (`getSignupUrl` in
 * `apps/landing/src/lib/url-config.ts`). Descope's flow redirects for social
 * login and the query is gone by the time the first run renders, so the plan
 * is kept in `sessionStorage`, as the scanner's host is (`site-prefill.ts`):
 * same origin, so it survives those redirects, and gone when the tab closes.
 * The first run reads it once the site exists and starts checkout for it,
 * then forgets it, so a later visit never sends anyone to Stripe again.
 *
 * Only a plan anyone can buy and an interval Stripe has a price for are
 * kept, and they are re-checked on the way out: storage is writable by any
 * script on the origin, and a checkout should start only for a plan the
 * visitor chose.
 */
import { isBillingInterval, isSelfServePlan } from "@headerhawk/core/plans";
import type { BillingInterval, SelfServePlan } from "@headerhawk/core/plans";

const KEY = "onboarding:signup-plan";

export interface SignupPlan {
  plan: SelfServePlan;
  interval: BillingInterval;
}

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Storage can throw in a private window or with site data blocked; the
    // visitor then lands on Free and can start a trial from Billing.
    return undefined;
  }
}

function parse(plan: unknown, interval: unknown): SignupPlan | undefined {
  if (typeof plan !== "string" || !isSelfServePlan(plan)) return undefined;
  return {
    plan,
    // The pricing page always sends one; a link without it means monthly,
    // as `getSignupUrl` defaults it.
    interval: isBillingInterval(interval) ? interval : "monthly",
  };
}

/** The `?plan=` and `?period=` of a signup link, or undefined. */
export function planFromQuery(search: string): SignupPlan | undefined {
  const params = new URLSearchParams(search);
  return parse(params.get("plan"), params.get("period"));
}

/** Remember the plan a signup link carried, if it carried a valid one. */
export function captureSignupPlan(search: string): void {
  const chosen = planFromQuery(search);
  if (!chosen) return;
  try {
    storage()?.setItem(KEY, JSON.stringify(chosen));
  } catch {
    // Quota or blocked storage: the convenience is lost, nothing else.
  }
}

/** The remembered plan, if any. */
export function readSignupPlan(): SignupPlan | undefined {
  try {
    const raw = storage()?.getItem(KEY);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as Partial<Record<keyof SignupPlan, unknown>>;
    return parse(value.plan, value.interval);
  } catch {
    return undefined;
  }
}

export function clearSignupPlan(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}
